import { useAuthContext } from 'src/context/auth.context';
import clsx from 'clsx';
import { Card, Typography } from '@otp/ui';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Routes } from 'src/components/common/router/constants';
import {
  Actions,
  AppArea,
  Subjects,
  WttpDashboardDocument,
} from 'src/graphql/generated/graphql';
import { useQuery } from '@apollo/client';
import { getFullName } from 'src/utils/user-data';
import { Configure } from '@otp/icons';
import { useDeviceContext } from 'src/context/device.context';
import { useCan } from 'src/utils/can';

export const WttpConfiguration = () => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'wttp.configuration',
  });
  const { can } = useCan();
  const { isAdmin } = useAuthContext();
  const { isMobile } = useDeviceContext();

  const navigate = useNavigate();

  const { data } = useQuery(WttpDashboardDocument);
  if (!data) {
    return null;
  }

  const questions = data.wttpDashboard.questionsCount;
  const questionLastUser =
    data.wttpDashboard.lastUpdatedQuestion?.updatedByUser;

  const questionnaires = data.wttpDashboard.questionnairesCount;
  const questionnaireLastUser =
    data.wttpDashboard.lastUpdatedQuestionnaire?.updatedByUser;

  const templates = data.wttpDashboard.templatesCount;
  const templateLastUser =
    data.wttpDashboard.lastUpdatedTemplate?.updatedByUser;

  const canWriteReadTemplates =
    can({
      appArea: AppArea.Wttp,
      action: Actions.Write,
      subject: Subjects.Templates,
    }) ||
    can({
      appArea: AppArea.Wttp,
      action: Actions.Read,
      subject: Subjects.Templates,
    });
  const canWriteReadQuestionnaires =
    can({
      appArea: AppArea.Wttp,
      action: Actions.Write,
      subject: Subjects.Questionnaires,
    }) ||
    can({
      appArea: AppArea.Wttp,
      action: Actions.Read,
      subject: Subjects.Questionnaires,
    });
  const canWriteReadQuestions =
    can({
      appArea: AppArea.Wttp,
      action: Actions.Write,
      subject: Subjects.Questions,
    }) ||
    can({
      appArea: AppArea.Wttp,
      action: Actions.Read,
      subject: Subjects.Questions,
    });

  const hasAnyConfigurationAccess =
    canWriteReadTemplates ||
    canWriteReadQuestionnaires ||
    canWriteReadQuestions;

  if (!hasAnyConfigurationAccess) {
    return null;
  }

  return (
    <Card noBorder className="flex flex-col w-full bg-white">
      <Typography variant="subtitle1" className="mb-2 font-bold">
        {t('title')}
      </Typography>
      <Typography variant="subtitle2">{t('description')}</Typography>

      <div
        className={clsx(
          {
            'flex-col': isMobile,
            'flex-row': !isMobile,
            'flex-row-reverse': !isAdmin,
          },
          'flex flex-grow gap-2 mt-4',
        )}
      >
        {canWriteReadQuestions && (
          <Card
            className="flex flex-col flex-1 h-48 w-auto"
            isLink={true}
            onClick={() => navigate(Routes.wttpQuestions.path)}
          >
            <Typography
              variant="subtitle1"
              className="mb-2 font-bold flex gap-2"
            >
              <Configure />
              {isAdmin && t('cards.questions.title-admin')}
              {!isAdmin && t('cards.questions.title')}
            </Typography>

            <div className="flex-grow"></div>

            <Typography variant="subtitle2" className="font-bold">
              {questions}{' '}
              {questions === 1
                ? t('cards.questions.question')
                : t('cards.questions.questions')}
            </Typography>
            {questionLastUser && (
              <Typography variant="subtitle2">
                {t('last-update')} {getFullName(questionLastUser)}
              </Typography>
            )}
          </Card>
        )}
        {canWriteReadQuestionnaires && (
          <Card
            className="flex flex-col flex-1 h-48 w-auto"
            isLink={true}
            onClick={() => navigate(Routes.wttpQuestionnaires.path)}
          >
            <Typography
              variant="subtitle1"
              className="mb-2 font-bold flex gap-2"
            >
              <Configure />
              {isAdmin && t('cards.questionnaires.title-admin')}
              {!isAdmin && t('cards.questionnaires.title')}
            </Typography>

            <div className="flex-grow"></div>

            <Typography variant="subtitle2" className="font-bold">
              {questionnaires}{' '}
              {questionnaires === 1
                ? t('cards.questionnaires.questionnaire')
                : t('cards.questionnaires.questionnaires')}
            </Typography>
            {questionnaireLastUser && (
              <Typography variant="subtitle2">
                {t('last-update')} {getFullName(questionnaireLastUser)}
              </Typography>
            )}
          </Card>
        )}
        {canWriteReadTemplates && (
          <Card
            className="flex flex-col flex-1 h-48 w-auto"
            isLink={true}
            onClick={() => navigate(Routes.wttpTemplates.path)}
          >
            <Typography
              variant="subtitle1"
              className="mb-2 font-bold flex gap-2"
            >
              <Configure />
              {isAdmin && t('cards.templates.title-admin')}
              {!isAdmin && t('cards.templates.title')}
            </Typography>

            <div className="flex-grow"></div>

            <Typography variant="subtitle2" className="font-bold">
              {templates}{' '}
              {templates === 1
                ? t('cards.templates.template')
                : t('cards.templates.templates')}
            </Typography>
            {templateLastUser && (
              <Typography variant="subtitle2">
                {t('last-update')} {getFullName(templateLastUser)}
              </Typography>
            )}
          </Card>
        )}
      </div>
    </Card>
  );
};
